import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, BadgeCheck, ClipboardList, Mail, Users } from "lucide-react";
import { ASSETS } from "@/data/content";
import { api, errorText } from "@/lib/api";

const POSITIONS = [
  "Las Venturas Police Department",
  "National Medical Center",
  "Quick Fix Garage",
  "Barren County Parking Supervision",
  "Adminisztrátor",
  "BETA teszter",
];

export default function PositionApplicationPage() {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (busy || sent) return;
    const values = new FormData(event.currentTarget);
    const payload = {
      name: values.get("name").trim(),
      age: Number(values.get("age")),
      discord: values.get("discord").trim(),
      position: values.get("position"),
      introduction: values.get("introduction").trim(),
    };
    if (!payload.name || !payload.discord || !payload.introduction) {
      setError("Kérjük, tölts ki minden szöveges mezőt; csak szóközöket nem adhatsz meg.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/position-applications", payload);
      if (data?.accepted !== true) {
        throw new Error("A szerver nem igazolta vissza a beküldést. Kérjük, ellenőrizd később, mielőtt újraküldöd.");
      }
      setSent(true);
    } catch (ex) {
      setError(errorText(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="position-page relative min-h-screen text-white" data-testid="position-application-page">
      <div className="position-page-glow pointer-events-none absolute inset-0" aria-hidden="true" />
      <header className="relative mx-auto flex max-w-[1280px] items-center justify-between gap-4 px-5 py-6 sm:px-8">
        <Link to="/" aria-label="BCRP főoldal" className="flex items-center gap-3">
          <img src={ASSETS.logo} alt="" className="h-11 w-11 object-contain" />
          <span className="font-display text-sm font-bold tracking-[0.2em]">BCRP</span>
        </Link>
        <Link to="/" className="position-back inline-flex items-center gap-2 text-xs text-white/60 transition-colors hover:text-white sm:text-sm">
          <ArrowLeft size={16} aria-hidden="true" />
          Vissza a főoldalra
        </Link>
      </header>

      <main className="relative mx-auto grid max-w-[1280px] gap-10 px-5 pb-16 pt-8 sm:px-8 sm:pt-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16 lg:pb-24">
        <section className="lg:pt-5" aria-labelledby="position-title">
          <span className="mb-7 inline-flex items-center gap-2 rounded-full border border-red-500/25 bg-red-500/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-red-300">
            <Users size={14} aria-hidden="true" />
            Pályázatok
          </span>
          <h1 id="position-title" className="font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            A következő<br />
            fejezetet<br />
            <span className="text-red-500">te vezeted.</span>
          </h1>
          <p className="mt-6 max-w-sm text-sm leading-7 text-white/55">
            Építs csapatot, alakíts történeteket, és hozz új lendületet Barren County életébe.
            Mutasd meg, miért rád bízzuk a pozíciót!
          </p>

          <div className="mt-9 space-y-5 border-t border-white/10 pt-7">
            {[
              ["01", "Válassz pozíciót", "Találd meg a hozzád illő szerepet."],
              ["02", "Mutatkozz be", "Mesélj a tapasztalatodról és a terveidről."],
              ["03", "Tedd meg az első lépést", "Egy jó történet egy jó csapattaggal kezdődik."],
            ].map(([number, title, description]) => (
              <div key={number} className="flex items-start gap-4">
                <span className="font-mono pt-0.5 text-xs text-red-500">{number}</span>
                <div>
                  <h2 className="text-sm font-semibold">{title}</h2>
                  <p className="mt-1 text-xs leading-5 text-white/45">{description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-9 flex items-start gap-3 text-white/45">
            <Mail size={17} className="mt-0.5 shrink-0 text-red-500/70" aria-hidden="true" />
            <p className="text-xs leading-6">
              A pályázatod címzettje<br />
              <span className="text-white/80">support@bc-rp.hu</span>
            </p>
          </div>
        </section>

        <section className="position-form-card min-w-0 rounded-3xl border border-white/10 p-5 sm:p-8" aria-labelledby="application-title">
          <div className="mb-7 flex items-start justify-between gap-3 border-b border-white/10 pb-6">
            <div>
              <h2 id="application-title" className="font-display text-2xl font-bold">Pályázatok</h2>
              <p className="mt-2 text-xs leading-5 text-white/45">A következő nagy történet veled kezdődhet.</p>
            </div>
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-red-500/20 bg-red-500/10 text-red-500">
              <ClipboardList size={21} aria-hidden="true" />
            </span>
          </div>

          <form onSubmit={submit} className="space-y-5" aria-busy={busy}>
            <fieldset disabled={busy || sent} className="min-w-0 space-y-5">
            <div className="grid gap-5 sm:grid-cols-[1fr_0.45fr]">
              <div>
                <label htmlFor="applicant-name" className="position-label">Név <span>*</span></label>
                <input id="applicant-name" name="name" autoComplete="name" maxLength={100} pattern={".*\\S.*"} placeholder="Add meg a neved" required className="position-input" />
              </div>
              <div>
                <label htmlFor="applicant-age" className="position-label">Életkor <span>*</span></label>
                <input id="applicant-age" name="age" type="number" min="1" max="120" step="1" placeholder="Életkorod" required className="position-input" />
              </div>
            </div>
            <div>
              <label htmlFor="applicant-discord" className="position-label">Discord név <span>*</span></label>
              <input id="applicant-discord" name="discord" autoComplete="off" maxLength={100} pattern={".*\\S.*"} placeholder="Discord felhasználóneved" required className="position-input" />
            </div>
            <div>
              <label htmlFor="applicant-position" className="position-label">Pályázott pozíció <span>*</span></label>
              <select id="applicant-position" name="position" defaultValue="" required className="position-input">
                <option value="" disabled>Válassz pozíciót</option>
                {POSITIONS.map((position) => <option key={position} value={position}>{position}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="applicant-introduction" className="position-label">Bemutatkozás és motiváció <span>*</span></label>
              <p id="introduction-help" className="mb-3 text-xs leading-6 text-white/45">
                Írj röviden magadról, a korábbi tapasztalataidról, és arról,
                miért téged válasszunk a pozícióra.
              </p>
              <textarea
                id="applicant-introduction"
                name="introduction"
                rows={7}
                maxLength={10000}
                required
                aria-describedby="introduction-help"
                placeholder={"Rövid bemutatkozás…\n\nKorábbi tapasztalataim…\n\nMiért engem válasszatok…"}
                className="position-input resize-y leading-6"
              />
            </div>

            <div className="rounded-xl border border-red-500/15 bg-red-500/[0.06] px-4 py-3 text-xs leading-6 text-white/60">
              A megadott adatokat a support@bc-rp.hu címre küldjük a pályázatod elbírálásához.
              Kérjük, jelszót és egyéb bizalmas adatot ne adj meg!
            </div>
            <button type="submit" className="btn-ucp btn-red flex w-full items-center justify-center gap-3 px-5 py-4 text-sm font-semibold">
              {busy ? "Küldés folyamatban…" : sent ? "Pályázat elküldve" : "Pályázat beküldése"}
              <ArrowUpRight size={18} aria-hidden="true" />
            </button>
            </fieldset>
            {error && <p role="alert" className="rounded-xl border border-red-400/25 bg-red-400/10 px-4 py-3 text-sm leading-6 text-red-300">{error}</p>}
            {sent && (
              <p role="status" className="flex items-start gap-2 text-sm leading-6 text-red-300">
                <BadgeCheck size={19} className="mt-0.5 shrink-0" aria-hidden="true" />
                A pályázatodat továbbítottuk a support@bc-rp.hu címre. A megadott Discord neveden kereshetünk a továbbiakkal kapcsolatban.
              </p>
            )}
            <p className="text-center text-[11px] text-white/40">A csillaggal jelölt mezők kitöltése kötelező.</p>
          </form>
        </section>
      </main>
      <footer className="relative border-t border-white/[0.06] px-5 py-6 text-center text-[10px] uppercase tracking-[0.2em] text-white/30">
        Barren County Roleplay · Pályázatok
      </footer>
    </div>
  );
}
